import HttpClient from "./httpClient";

/** One row of the checklist, as the server folds it from the job's ledger. */
export interface OnboardingStep {
  name: string;
  title: string;
  /** Which console the step drives; "api" is the one step with no browser and so no recording. */
  console: "admin" | "cloud" | "api" | string | null;
  state: "pending" | "running" | "done" | "failed";
  attempts: number;
  error: string | null;
  /** A sentence about a step that did something other than the plain thing: reused an account, skipped itself. */
  note: string | null;
  session_id: string | null;
  has_recording: boolean;
  started_at: string | null;
  finished_at: string | null;
}

export interface OnboardingRun {
  /** The newest job of the run; each step is its own job. */
  job_id: number;
  /** The first job of the run, shared by every step's job. */
  run_id: number;
  parent_job_id: number | null;
  /** The run's status, not the row's: every step's row settles; the summary says what the run is doing. */
  status: "running" | "completed" | "failed" | string;
  steps: OnboardingStep[];
  current_step: string | null;
  admin_email: string | null;
  /** The typed address already existed as a Super Admin and the run adopted it instead of creating it (Q149). */
  reused: boolean;
  project_id: string | null;
  service_account_email: string | null;
  /** True from the key step on: the service-account JSON is in application_config. */
  configuration_saved: boolean;
  /**
   * Why a failed run stopped. Two of them leave [Start] available: ``bad_admin_email``, which
   * the technician fixes by typing a different address (Q162), and ``not_an_admin``, which they
   * fix by signing in as somebody who can administer the tenant (Q163).
   */
  failure: { reason: "sign_in_required" | "step_failed" | "bad_admin_email" | "not_an_admin" | string; message: string } | null;
  /** Set when the technician finished a failed run by hand and saved after a green Test. */
  manually_completed_at: string | null;
  attempt: number;
  created_at: string | null;
  completed_at: string | null;
  resumed_from: number | null;
}

export interface SignInSession {
  session_id: string;
  /** The provider's live view. Loaded straight into the iframe; never proxied. */
  live_url: string;
  landed_on: string;
}

/**
 * The integrations-page onboarding flow: a two-step sign-in in a live browser, then a
 * background job polled from here. No thread is involved anywhere.
 */
class GWorkspaceOnboardingApi extends HttpClient {
  private base = (customerId: string | number, applicationId: string | number) =>
    `/customers/${customerId}/applications/${applicationId}/onboarding`;

  startSignIn = (customerId: string | number, applicationId: string | number) =>
    this.post(`${this.base(customerId, applicationId)}/sign-in/start`, {}, {}) as Promise<SignInSession>;

  nextSignIn = (customerId: string | number, applicationId: string | number, sessionId: string) =>
    this.post(`${this.base(customerId, applicationId)}/sign-in/next`, {}, { session_id: sessionId }) as Promise<{
      landed_on: string;
      stopped: boolean;
    }>;

  /** Stops the session (which saves the profile) and enqueues the first job in one go. */
  finishSignIn = (customerId: string | number, applicationId: string | number, sessionId: string, adminEmail: string, assignLicense = true) =>
    this.post(`${this.base(customerId, applicationId)}/sign-in/done`, {}, {
      session_id: sessionId,
      admin_email: adminEmail,
      assign_license: assignLicense,
    }) as Promise<{ stopped: boolean; job_id: number }>;

  cancelSignIn = (customerId: string | number, applicationId: string | number, sessionId: string) =>
    this.post(`${this.base(customerId, applicationId)}/sign-in/cancel`, {}, { session_id: sessionId }) as Promise<{ stopped: boolean }>;

  /**
   * The newest run, and whether browser automation is switched on for this customer at all. The
   * flag rides along because the browser cannot evaluate it: the page's own LaunchDarkly context
   * is a user, and this is an ``msp``-kind flag (Q163).
   */
  getStatus = (customerId: string | number, applicationId: string | number) =>
    this.get(`${this.base(customerId, applicationId)}/status`) as Promise<{
      run: OnboardingRun | null;
      browser_use_enabled?: boolean;
    }>;

  getRecordingUrl = (customerId: string | number, applicationId: string | number, sessionId: string) =>
    this.get(`${this.base(customerId, applicationId)}/recording/${encodeURIComponent(sessionId)}`) as Promise<{
      url: string;
      thumbnail_url: string | null;
      expires_in_seconds: number;
    }>;
}

export default GWorkspaceOnboardingApi;
